// A shift as one document: the header, the attendants table and the totals; each section opens its
// own view (fuel & tanks, an attendant, lubes / tills / expenses). Same data and calculation as
// the current shift screen.
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { shiftLabel, isSunday } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import useShiftDoc, { num, uid } from "@/v2/useShiftDoc";
import { Page, Btn, Table, Tr, Td, NumIn, TextIn, Field, Totals, StatusPill, money, signed, tone, Loading } from "@/v2/ui";

const fmtDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
function hours(date, type) {
  const d = new Date(`${date}T12:00:00`);
  const prev = new Date(d.getTime() - 86400000);
  const f = (x) => x.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  if (type === "AM") return `${f(prev)} ${isSunday(prev.toISOString().slice(0, 10)) ? "1PM" : "4PM"} – ${f(d)} 8AM`;
  if (isSunday(date)) return `${f(prev)} 4PM – ${f(d)} 1PM`;
  return `${f(d)} 8AM – 4PM`;
}

export default function Shift2() {
  const { id, view = "overview", sub } = useParams();
  const doc = useShiftDoc(id);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [note, setNote] = useState("");
  if (doc.loadError) return <Page title="Shift" back={{ to: "/v2/shifts", label: "Shifts" }}><p className="mt-6 text-rose-700">{doc.loadError}</p></Page>;
  if (!doc.computed) return <Page title="Shift"><Loading /></Page>;
  const { shift, readOnly } = doc;
  const title = `Shift ${fmtDate(shift.date)} · ${shiftLabel(shift.shift_type, shift.date)}`;
  const base = `/v2/shift/${id}`;

  const pdf = async () => {
    const res = await api.get(`/reports/shift/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `shift_${shift.date}_${shiftLabel(shift.shift_type, shift.date)}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  const state = readOnly ? null : doc.saveError ? <span className="text-sm text-rose-700">Not saved</span>
    : doc.saving || doc.dirty ? <span className="text-sm text-slate-400">Saving…</span> : <span className="text-sm text-slate-400">Saved</span>;
  const actions = (
    <>
      {state}
      <Btn onClick={pdf}>PDF</Btn>
      {!readOnly && <Btn kind="outline" onClick={() => doc.save()}>Save</Btn>}
      {!readOnly && <Btn kind="primary" onClick={() => setSubmitOpen(true)}>Submit</Btn>}
    </>
  );
  const back = view === "overview" ? { to: "/v2/shifts", label: "Shifts" } : { to: base, label: title };

  return (
    <Page title={view === "overview" ? <span className="flex flex-wrap items-center gap-3">{title}<StatusPill status={shift.status} /></span> : VIEW_TITLES[view]} back={back} actions={actions}>
      {view === "overview" && <Overview doc={doc} base={base} />}
      {view === "fuel" && <FuelView doc={doc} />}
      {view === "attendant" && <AttendantView doc={doc} base={base} attId={sub} />}
      {view === "stock" && <StockView doc={doc} />}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Submit {title}</DialogTitle></DialogHeader>
          <Totals rows={[["Cash expected", money(doc.computed.totals.expected_cash)], ["Cash counted", money(doc.computed.totals.actual_cash)],
            ["Over / short", signed(doc.computed.totals.cash_variance), tone(doc.computed.totals.cash_variance)]]} />
          <Field label="Note"><textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-md border border-slate-300 p-2 text-sm text-slate-800" /></Field>
          <DialogFooter>
            <Btn onClick={() => setSubmitOpen(false)}>Cancel</Btn>
            <Btn kind="primary" onClick={async () => { if (await doc.submit(note)) setSubmitOpen(false); }}>Submit</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
const VIEW_TITLES = { fuel: "Fuel sales", attendant: "Attendants", stock: "Lubes & LPG, tills, expenses" };

// ---------- helpers shared by the views ----------
function useLookups(doc) {
  const { cfg, entry } = doc;
  return useMemo(() => {
    const att = Object.fromEntries(cfg.attendants.map((a) => [a.id, a]));
    const methods = cfg.payment_methods.filter((m) => m.active !== false);
    const linked = new Set((cfg.receivable_accounts || []).filter((a) => a.method_id).map((a) => a.method_id));
    const invoiceMethod = methods.find((m) => m.kind === "invoice" && !linked.has(m.id));
    // attendants on this shift: anyone with a Vivo figure (even blank), money, nozzles or a till
    const on = new Set(Object.keys(entry.attendant_expected || {}));
    entry.payments.forEach((p) => { if (p.attendant_id && num(p.amount)) on.add(p.attendant_id); });
    entry.nonfuel_payments.forEach((p) => p.attendant_id && on.add(p.attendant_id));
    Object.values(entry.pump_attendants || {}).forEach((a) => a && on.add(a));
    entry.invoice_lines.forEach((l) => l.attendant_id && on.add(l.attendant_id));
    const onShift = cfg.attendants.filter((a) => on.has(a.id));
    return { att, methods, linked, invoiceMethod, onShift };
  }, [cfg, entry]);
}
const payRow = (entry, attId, mid) => entry.payments.findIndex((p) => (p.attendant_id || "") === (attId || "") && p.method_id === mid);

// ---------- overview ----------
function Overview({ doc, base }) {
  const { cfg, entry, computed, update, readOnly, shift } = doc;
  const t = computed.totals;
  const { att, methods, onShift } = useLookups(doc);
  const [adding, setAdding] = useState("");
  const lines = Object.fromEntries(computed.attendant_lines.map((l) => [l.attendant_id || "", l]));
  const shown = methods.filter((m) => ["cash", "mobile_money", "card", "invoice"].includes(m.kind) && m.name !== "Montrose"
    || entry.payments.some((p) => p.method_id === m.id && num(p.amount)));
  const nfBy = {};
  entry.nonfuel_payments.forEach((p) => { nfBy[p.attendant_id || ""] = (nfBy[p.attendant_id || ""] || 0) + num(p.amount); });
  const colTotal = (mid) => entry.payments.filter((p) => p.method_id === mid).reduce((a, p) => a + num(p.amount), 0);
  const litres = computed.fuel_lines.reduce((a, l) => a + l.liters_sold, 0);
  const sold = computed.nonfuel_lines.reduce((a, l) => a + (l.sold > 0 ? l.sold : 0), 0);
  const tillDiff = computed.till_lines.reduce((a, l) => a + (l.variance || 0), 0);
  const invLines = entry.invoice_lines.filter((l) => num(l.amount));
  const firstInvAtt = invLines[0]?.attendant_id || onShift[0]?.id;
  const lpgSold = computed.nonfuel_group_lines.find((g) => g.group === "lpg_accessory")?.sold_revenue || 0;
  const lpgCash = computed.nonfuel_payment_lines.filter((l) => l.group === "lpg_accessory" && l.kind === "cash").reduce((a, l) => a + l.amount, 0);

  return (
    <>
      <div className="mt-5 grid gap-4 border-b border-slate-200 pb-5 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Date"><div className="flex min-h-[40px] items-center text-sm text-slate-800">{fmtDate(shift.date)}</div></Field>
        <Field label="Shift"><div className="flex min-h-[40px] items-center text-sm text-slate-800">{shiftLabel(shift.shift_type, shift.date)}</div></Field>
        <Field label="Supervisor"><TextIn value={entry.supervisor_name} disabled={readOnly} onChange={(v) => update((d) => { d.supervisor_name = v; })} className="min-h-[40px] text-sm text-slate-800" /></Field>
        <Field label="Hours"><div className="flex min-h-[40px] items-center text-sm text-slate-800">{hours(shift.date, shift.shift_type)}</div></Field>
      </div>

      <h2 className="mt-6 text-base font-semibold text-slate-900">Attendants</h2>
      <Table minWidth={900} cols={[{ label: "Attendant" }, { label: cfg.station?.pos_name || "Vivo", align: "right" },
        ...shown.map((m) => ({ label: m.name, align: "right" })), { label: "Lubes & LPG", align: "right" }, { label: "Total", align: "right" }, { label: "Over / short", align: "right" }]}
        foot={onShift.length > 0 && (
          <tr>
            <td className="px-2 py-2.5">Total</td>
            <td className="px-2 py-2.5 text-right tabular-nums">{money(Object.values(entry.attendant_expected || {}).reduce((a, v) => a + num(v), 0))}</td>
            {shown.map((m) => <td key={m.id} className="px-2 py-2.5 text-right tabular-nums">{money(colTotal(m.id))}</td>)}
            <td className="px-2 py-2.5 text-right tabular-nums">{money(Object.values(nfBy).reduce((a, v) => a + v, 0))}</td>
            <td className="px-2 py-2.5 text-right tabular-nums">{money(computed.attendant_lines.reduce((a, l) => a + l.collected_total, 0) + Object.values(nfBy).reduce((a, v) => a + v, 0))}</td>
            <td className="px-2 py-2.5 text-right tabular-nums">{signed(computed.attendant_lines.reduce((a, l) => a + l.variance, 0))}</td>
          </tr>
        )}>
        {onShift.map((a) => {
          const l = lines[a.id] || { collected_total: 0, variance: 0 };
          return (
            <Tr key={a.id}>
              <Td><Link to={`${base}/attendant/${a.id}`} className="font-semibold text-sky-700 hover:underline">{a.name}</Link></Td>
              <Td right><NumIn label={`${a.name} Vivo`} value={entry.attendant_expected?.[a.id] ?? ""} disabled={readOnly} width="w-[104px]"
                onChange={(v) => update((d) => { d.attendant_expected[a.id] = v; })} /></Td>
              {shown.map((m) => {
                const i = payRow(entry, a.id, m.id);
                return <Td key={m.id} right>{i < 0 ? "—" : <NumIn label={`${a.name} ${m.name}`} value={entry.payments[i].amount} disabled={readOnly} width="w-[92px]"
                  onChange={(v) => update((d) => { d.payments[i].amount = v; })} />}</Td>;
              })}
              <Td right>{nfBy[a.id] ? money(nfBy[a.id]) : <span className="text-slate-300">—</span>}</Td>
              <Td right className="font-semibold">{money(l.collected_total + (nfBy[a.id] || 0))}</Td>
              <Td right className={tone(l.variance)}>{signed(l.variance)}</Td>
            </Tr>
          );
        })}
        {onShift.length === 0 && <tr><td colSpan={shown.length + 5} className="px-2 py-6 text-center text-slate-400">No attendants yet</td></tr>}
      </Table>
      {!readOnly && (
        <div className="mt-2 flex items-center gap-2">
          <select aria-label="Add attendant" value={adding} onChange={(e) => setAdding(e.target.value)} className="min-h-[36px] rounded-md border border-slate-300 bg-white px-2 text-sm">
            <option value="">Add attendant…</option>
            {cfg.attendants.filter((a) => a.active !== false && !onShift.some((o) => o.id === a.id)).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          {adding && <Btn kind="link" onClick={() => { update((d) => { d.attendant_expected[adding] = ""; }); setAdding(""); }}>Add</Btn>}
        </div>
      )}

      <div className="mt-4 border-t border-slate-200">
        {[
          ["Fuel sales", `${litres.toLocaleString(undefined, { maximumFractionDigits: 2 })} L`, money(t.fuel_sales_total), "", `${base}/fuel`],
          ["Tanks", `${computed.tank_lines.length} tanks`, `${signed(t.total_tank_variance_l).replace(/\.00$/, "")} L`, Math.abs(t.total_tank_variance_l) > 50 ? "text-amber-700" : "", `${base}/fuel`],
          ["Lubes & LPG", `${sold} sold`, money(t.nonfuel_revenue_total), "", `${base}/stock`],
          ["M-Pesa tills", `${computed.till_lines.length} tills`, signed(tillDiff), tone(tillDiff), `${base}/stock`],
          ["Invoices", `${invLines.length}`, money(invLines.reduce((a, l) => a + num(l.amount), 0)), "", firstInvAtt ? `${base}/attendant/${firstInvAtt}` : `${base}/attendant`],
          ["Expenses", `${entry.expenses.length}`, money(t.expenses_total), "", `${base}/stock`],
        ].map(([label, mid, amt, cls, to]) => (
          <Link key={label} to={to} className="grid min-h-[52px] grid-cols-[24px_minmax(0,1fr)_auto_auto] items-center gap-3 border-b border-slate-200 py-3 hover:bg-slate-50">
            <span aria-hidden="true" className="text-slate-400">▸</span>
            <span className="text-[15px] font-semibold text-slate-900">{label}</span>
            <span className="text-sm text-slate-500">{mid}</span>
            <span className={`min-w-[120px] text-right text-sm font-semibold tabular-nums ${cls}`}>{amt}</span>
          </Link>
        ))}
      </div>

      <div className="mt-7 flex flex-wrap items-start justify-between gap-8">
        <Field label="Notes">
          <textarea rows={4} value={entry.notes} disabled={readOnly} onChange={(e) => { const v = e.target.value; update((d) => { d.notes = v; }); }}
            className="w-full min-w-[300px] rounded-md border border-slate-300 p-2.5 text-sm text-slate-800 md:w-[420px]" />
        </Field>
        <div className="flex flex-col gap-4">
          <Totals rows={[
            ["Sales", money(t.total_sales)],
            ["Less non-cash", money(t.non_cash_total)],
            ...(t.expenses_total ? [["Less expenses", money(t.expenses_total)]] : []),
            ["Cash expected", money(t.expected_cash)],
            ["Cash counted", money(t.actual_cash)],
            ["Over / short", signed(t.cash_variance), tone(t.cash_variance, cfg.station?.cash_tolerance || 100)],
          ]} />
          {cfg.station?.lpg_cash_separate && (
            <div className="grid max-w-[380px] grid-cols-[minmax(0,1fr)_auto] gap-x-6 text-sm text-slate-500">
              <span>of which fuel &amp; lubes</span><span className="text-right tabular-nums">{signed(t.cash_variance - (lpgCash - lpgSold))}</span>
              <span>of which LPG</span><span className="text-right tabular-nums">{signed(lpgCash - lpgSold)}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ---------- fuel sales & tanks ----------
function FuelView({ doc }) {
  const { cfg, entry, computed, update, readOnly } = doc;
  const pumps = Object.fromEntries(cfg.pumps.map((p) => [p.id, p]));
  const tanks = Object.fromEntries(cfg.tanks.map((x) => [x.id, x]));
  const products = Object.fromEntries(cfg.products.map((p) => [p.id, p]));
  const att = Object.fromEntries(cfg.attendants.map((a) => [a.id, a]));
  const lines = Object.fromEntries(computed.fuel_lines.map((l) => [l.pump_id, l]));
  const tl = Object.fromEntries(computed.tank_lines.map((l) => [l.tank_id, l]));
  const pname = (id) => (products[id]?.name || "").replace(/\s*\(.*\)/, "");
  return (
    <>
      <Table minWidth={900} cols={[{ label: "Nozzle" }, { label: "Attendant" }, { label: "Opening", align: "right" }, { label: "Closing", align: "right" },
        { label: "Manual closing", align: "right" }, { label: "Litres", align: "right" }, { label: "Price", align: "right" }, { label: "Amount", align: "right" }]}
        foot={<tr><td className="px-2 py-2.5" colSpan={5}>Total</td><td className="px-2 py-2.5 text-right tabular-nums">{computed.fuel_lines.reduce((a, l) => a + l.liters_sold, 0).toFixed(2)}</td><td /><td className="px-2 py-2.5 text-right tabular-nums">{money(computed.totals.fuel_sales_total)}</td></tr>}>
        {entry.fuel_readings.map((r, i) => {
          const l = lines[r.pump_id] || {};
          return (
            <Tr key={r.pump_id}>
              <Td>{(pumps[r.pump_id]?.label || "").split(" ")[0]} · {pname(r.product_id)}</Td>
              <Td><select aria-label="Attendant" disabled={readOnly} value={entry.pump_attendants?.[r.pump_id] || ""} onChange={(e) => { const v = e.target.value; update((d) => { d.pump_attendants[r.pump_id] = v; }); }}
                className="min-h-[34px] rounded-md border border-slate-300 bg-white px-1.5 text-sm disabled:border-transparent disabled:bg-transparent">
                <option value="">—</option>{cfg.attendants.filter((a) => a.active !== false).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>{entry.pump_attendants?.[r.pump_id] && !att[entry.pump_attendants[r.pump_id]] ? "?" : null}</Td>
              <Td right>{r._elecOpeningLocked || readOnly ? <span className="text-slate-500">{num(r.elec_opening_l).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                : <NumIn label="Opening" value={r.elec_opening_l} onChange={(v) => update((d) => { d.fuel_readings[i].elec_opening_l = v; })} width="w-32" />}</Td>
              <Td right><NumIn label="Closing" value={r.elec_closing_l} disabled={readOnly} onChange={(v) => update((d) => { d.fuel_readings[i].elec_closing_l = v; })} width="w-32" /></Td>
              <Td right><NumIn label="Manual closing" value={r.closing} disabled={readOnly} onChange={(v) => update((d) => { d.fuel_readings[i].closing = v; })} width="w-28" /></Td>
              <Td right>{(l.liters_sold || 0).toFixed(2)}</Td>
              <Td right>{money(l.unit_price || 0)}</Td>
              <Td right>{money(l.amount || 0)}</Td>
            </Tr>
          );
        })}
      </Table>

      <h2 className="mt-9 text-xl font-semibold text-slate-900">Tanks</h2>
      <Table minWidth={900} cols={[{ label: "Tank" }, { label: "Opening dip", align: "right" }, { label: "Delivered", align: "right" }, { label: "Closing dip", align: "right" },
        { label: "Gauge closing", align: "right" }, { label: "Sold by dips", align: "right" }, { label: "Sold by pumps", align: "right" }, { label: "Difference", align: "right" }]}
        foot={<tr><td className="px-2 py-2.5" colSpan={5}>Total</td><td className="px-2 py-2.5 text-right tabular-nums">{computed.tank_lines.reduce((a, l) => a + l.dip_sales, 0).toLocaleString()}</td><td className="px-2 py-2.5 text-right tabular-nums">{Math.round(computed.tank_lines.reduce((a, l) => a + l.pump_throughput, 0)).toLocaleString()}</td><td className="px-2 py-2.5 text-right tabular-nums">{Math.round(computed.totals.total_tank_variance_l)} L</td></tr>}>
        {entry.tank_dips.map((r, i) => {
          const l = tl[r.tank_id] || {};
          return (
            <Tr key={r.tank_id}>
              <Td>{(tanks[r.tank_id]?.label || "").split(" ")[0]} · {pname(r.product_id)}</Td>
              <Td right>{r._openingLocked || readOnly ? <span className="text-slate-500">{num(r.opening_dip).toLocaleString()}</span>
                : <NumIn label="Opening dip" value={r.opening_dip} onChange={(v) => update((d) => { d.tank_dips[i].opening_dip = v; })} width="w-24" />}</Td>
              <Td right><NumIn label="Delivered" value={r.deliveries} disabled={readOnly} onChange={(v) => update((d) => { d.tank_dips[i].deliveries = v; })} width="w-24" /></Td>
              <Td right><NumIn label="Closing dip" value={r.closing_dip} disabled={readOnly} onChange={(v) => update((d) => { d.tank_dips[i].closing_dip = v; })} width="w-24" /></Td>
              <Td right><NumIn label="Gauge closing" value={r.tls_closing_dip} disabled={readOnly} onChange={(v) => update((d) => { d.tank_dips[i].tls_closing_dip = v; })} width="w-24" /></Td>
              <Td right>{(l.dip_sales || 0).toLocaleString()}</Td>
              <Td right>{Math.round(l.pump_throughput || 0).toLocaleString()}</Td>
              <Td right className={Math.abs(l.variance_l || 0) > 50 ? "text-amber-700" : ""}>{`${(l.variance_l || 0) > 0 ? "+" : ""}${Math.round(l.variance_l || 0)} L`}</Td>
            </Tr>
          );
        })}
      </Table>
    </>
  );
}

// ---------- one attendant's handover ----------
function AttendantView({ doc, base, attId }) {
  const { cfg, entry, computed, update, readOnly } = doc;
  const { methods, invoiceMethod, onShift } = useLookups(doc);
  const [extra, setExtra] = useState([]);
  const a = onShift.find((x) => x.id === attId) || onShift[0];
  if (!a) return <p className="mt-6 text-slate-500">No attendants on this shift yet.</p>;
  const line = computed.attendant_lines.find((l) => l.attendant_id === a.id) || { expected_total: 0, collected_total: 0, variance: 0, nonfuel_collected: 0 };
  const customers = (cfg.receivable_accounts || []).filter((x) => x.kind === "customer" && x.active !== false);
  const amountOf = (mid) => { const i = payRow(entry, a.id, mid); return i < 0 ? 0 : num(entry.payments[i].amount); };
  const setAmount = (mid, v) => update((d) => { const i = payRow(d, a.id, mid); if (i >= 0) d.payments[i].amount = v; });
  const cash = methods.find((m) => m.kind === "cash");
  const mpesa = methods.find((m) => m.kind === "mobile_money");
  const cards = methods.filter((m) => m.kind === "card");
  const others = methods.filter((m) => !["cash", "mobile_money", "card"].includes(m.kind) && m.id !== invoiceMethod?.id);
  const showOther = (m) => amountOf(m.id) || extra.includes(m.id);
  const invLines = entry.invoice_lines.map((l, i) => ({ ...l, i })).filter((l) => l.attendant_id === a.id && l.method_id === invoiceMethod?.id);
  const payout = methods.find((m) => m.kind === "payout");
  const nf = entry.nonfuel_payments.map((p, i) => ({ ...p, i })).filter((p) => p.attendant_id === a.id);

  return (
    <>
      <div role="tablist" className="mt-5 flex flex-wrap gap-6 border-b border-slate-200">
        {onShift.map((x) => (
          <Link key={x.id} to={`${base}/attendant/${x.id}`} role="tab" aria-selected={x.id === a.id}
            className={`min-h-[44px] border-b-2 py-2.5 text-sm ${x.id === a.id ? "border-brand font-semibold text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{x.name}</Link>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-2xl font-semibold text-slate-900">{a.name}</h2>
        <span className="text-sm text-slate-500">{entry.fuel_readings.filter((r) => entry.pump_attendants?.[r.pump_id] === a.id).map((r) => (cfg.pumps.find((p) => p.id === r.pump_id)?.label || "").split(" ")[0]).join(", ")}</span>
      </div>
      <div className="mt-5 grid max-w-3xl gap-4 sm:grid-cols-3">
        <Field label={cfg.station?.pos_name || "Vivo"}><NumIn label="Vivo" value={entry.attendant_expected?.[a.id] ?? ""} disabled={readOnly} width="w-full" className="min-h-[40px]" onChange={(v) => update((d) => { d.attendant_expected[a.id] = v; })} /></Field>
        {cash && <Field label="Cash"><NumIn label="Cash" value={entry.payments[payRow(entry, a.id, cash.id)]?.amount} disabled={readOnly} width="w-full" className="min-h-[40px]" onChange={(v) => setAmount(cash.id, v)} /></Field>}
        {mpesa && <Field label="M-Pesa"><NumIn label="M-Pesa" value={entry.payments[payRow(entry, a.id, mpesa.id)]?.amount} disabled={readOnly} width="w-full" className="min-h-[40px]" onChange={(v) => setAmount(mpesa.id, v)} /></Field>}
      </div>

      {invoiceMethod && (
        <section className="mt-8 max-w-4xl">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-base font-semibold text-slate-900">Invoices</h3>
            <span className="text-sm"><NumIn label="Invoice total" value={entry.payments[payRow(entry, a.id, invoiceMethod.id)]?.amount} disabled={readOnly} onChange={(v) => setAmount(invoiceMethod.id, v)} />
              {invLines.length > 0 && Math.abs(invLines.reduce((s, l) => s + num(l.amount), 0) - amountOf(invoiceMethod.id)) > 0.5 &&
                <span className="ml-2 text-amber-700">itemized {money(invLines.reduce((s, l) => s + num(l.amount), 0))}</span>}</span>
          </div>
          <Table minWidth={640} cols={[{ label: "Customer" }, { label: "Invoice no." }, { label: "Vehicle" }, { label: "Litres", align: "right" }, { label: "Amount", align: "right" }, { label: "" }]}>
            {invLines.map((l) => (
              <Tr key={l._k}>
                <Td><select aria-label="Customer" disabled={readOnly} value={l.customer_id || ""} onChange={(e) => { const v = e.target.value; update((d) => { d.invoice_lines[l.i].customer_id = v; }); }}
                  className="min-h-[34px] w-full rounded-md border border-slate-300 bg-white px-1.5 text-sm"><option value="">—</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Td>
                <Td><TextIn label="Invoice no." value={l.invoice_no} disabled={readOnly} width="w-28" onChange={(v) => update((d) => { d.invoice_lines[l.i].invoice_no = v; })} /></Td>
                <Td><TextIn label="Vehicle" value={l.vehicle_reg} disabled={readOnly} width="w-28" onChange={(v) => update((d) => { d.invoice_lines[l.i].vehicle_reg = v.toUpperCase(); })} /></Td>
                <Td right><NumIn label="Litres" value={l.litres || ""} disabled={readOnly} width="w-20" onChange={(v) => update((d) => { d.invoice_lines[l.i].litres = v; })} /></Td>
                <Td right><NumIn label="Amount" value={l.amount} disabled={readOnly} onChange={(v) => update((d) => { d.invoice_lines[l.i].amount = v; })} /></Td>
                <Td>{!readOnly && <button type="button" aria-label="Remove invoice" className="min-h-[34px] px-2 text-slate-400 hover:text-rose-600" onClick={() => update((d) => { d.invoice_lines.splice(l.i, 1); })}>×</button>}</Td>
              </Tr>
            ))}
          </Table>
          {!readOnly && <Btn kind="link" onClick={() => update((d) => { d.invoice_lines.push({ _k: uid(), attendant_id: a.id, method_id: invoiceMethod.id, customer_id: "", invoice_no: "", vehicle_reg: "", product_id: null, litres: "", amount: "" }); })}>+ Add invoice</Btn>}
        </section>
      )}

      {cards.map((m) => {
        const shell = m.name.toLowerCase().includes("shell");
        const rows = entry.payment_transactions.map((x, i) => ({ ...x, i })).filter((x) => (x.attendant_id || "") === a.id && x.method_id === m.id);
        if (!amountOf(m.id) && !rows.length && !extra.includes(m.id)) return null;
        const itemized = rows.reduce((s, x) => s + num(x.amount), 0);
        return (
          <section key={m.id} className="mt-7 max-w-4xl">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-base font-semibold text-slate-900">{m.name}</h3>
              <span className="text-sm"><NumIn label={`${m.name} total`} value={entry.payments[payRow(entry, a.id, m.id)]?.amount} disabled={readOnly} onChange={(v) => setAmount(m.id, v)} />
                {rows.length > 0 && Math.abs(itemized - amountOf(m.id)) > 0.5 && <span className="ml-2 text-amber-700">receipts {money(itemized)}</span>}</span>
            </div>
            <Table minWidth={600} cols={[{ label: "Date" }, ...(shell ? [{ label: "Product" }] : []), { label: "Receipt no." }, ...(!shell ? [{ label: "Authorization code" }] : []), { label: "Amount", align: "right" }, { label: "" }]}>
              {rows.map((x) => (
                <Tr key={x._k}>
                  <Td><input type="date" aria-label="Date" disabled={readOnly} value={x.txn_date || ""} onChange={(e) => { const v = e.target.value; update((d) => { d.payment_transactions[x.i].txn_date = v; }); }} className="min-h-[34px] rounded-md border border-slate-300 px-1.5 text-sm" /></Td>
                  {shell && <Td><select aria-label="Product" disabled={readOnly} value={x.product || ""} onChange={(e) => { const v = e.target.value; update((d) => { d.payment_transactions[x.i].product = v; }); }} className="min-h-[34px] rounded-md border border-slate-300 bg-white px-1.5 text-sm"><option value="">—</option>{["Diesel", "Super", "V-Power", "Lubricant"].map((p) => <option key={p}>{p}</option>)}</select></Td>}
                  <Td><TextIn label="Receipt no." value={x.reference} disabled={readOnly} width="w-28" onChange={(v) => update((d) => { d.payment_transactions[x.i].reference = v.toUpperCase(); })} /></Td>
                  {!shell && <Td><TextIn label="Authorization code" value={x.auth_code} disabled={readOnly} width="w-32" onChange={(v) => update((d) => { d.payment_transactions[x.i].auth_code = v.toUpperCase(); })} /></Td>}
                  <Td right><NumIn label="Amount" value={x.amount} disabled={readOnly} onChange={(v) => update((d) => { d.payment_transactions[x.i].amount = v; })} /></Td>
                  <Td>{!readOnly && <button type="button" aria-label="Remove receipt" className="min-h-[34px] px-2 text-slate-400 hover:text-rose-600" onClick={() => update((d) => { d.payment_transactions.splice(x.i, 1); })}>×</button>}</Td>
                </Tr>
              ))}
            </Table>
            {!readOnly && <Btn kind="link" onClick={() => update((d) => { d.payment_transactions.push({ _k: uid(), attendant_id: a.id, method_id: m.id, amount: "", reference: "", note: "", txn_date: doc.shift.date, auth_code: "", product: "" }); })}>+ Add receipt</Btn>}
          </section>
        );
      })}

      {others.filter(showOther).map((m) => (
        <section key={m.id} className="mt-7 max-w-4xl">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-base font-semibold text-slate-900">{m.name}</h3>
            <NumIn label={m.name} value={entry.payments[payRow(entry, a.id, m.id)]?.amount} disabled={readOnly} onChange={(v) => setAmount(m.id, v)} />
          </div>
          {payout && m.id === payout.id && (
            <ExpenseLines doc={doc} filter={(e) => e.paid_by === a.id} newRow={{ paid_by: a.id }} />
          )}
        </section>
      ))}

      <section className="mt-7 max-w-4xl">
        <h3 className="text-base font-semibold text-slate-900">Lubes &amp; LPG cash</h3>
        {nf.map((p) => (
          <div key={p._k} className="mt-2 flex flex-wrap items-center gap-3">
            <select aria-label="For" disabled={readOnly} value={p.group} onChange={(e) => { const v = e.target.value; update((d) => { d.nonfuel_payments[p.i].group = v; }); }} className="min-h-[34px] rounded-md border border-slate-300 bg-white px-1.5 text-sm">
              <option value="lube">Lubes</option><option value="lpg_accessory">LPG</option>
            </select>
            <NumIn label="Amount" value={p.amount} disabled={readOnly} onChange={(v) => update((d) => { d.nonfuel_payments[p.i].amount = v; })} />
            {!readOnly && <button type="button" aria-label="Remove" className="min-h-[34px] px-2 text-slate-400 hover:text-rose-600" onClick={() => update((d) => { d.nonfuel_payments.splice(p.i, 1); })}>×</button>}
          </div>
        ))}
        {!readOnly && <Btn kind="link" onClick={() => update((d) => { d.nonfuel_payments.push({ _k: uid(), attendant_id: a.id, group: "lube", method_id: cash?.id || "", amount: "", reference: "", till_id: "" }); })}>+ Lubes &amp; LPG cash</Btn>}
      </section>

      {!readOnly && (
        <div className="mt-6 flex flex-wrap gap-2">
          {[...cards, ...others].filter((m) => !(amountOf(m.id) || extra.includes(m.id) || entry.payment_transactions.some((x) => x.attendant_id === a.id && x.method_id === m.id))).map((m) => (
            <button key={m.id} type="button" onClick={() => setExtra((x) => [...x, m.id])} className="min-h-[36px] rounded-full border border-slate-200 bg-slate-50 px-3.5 text-[13px] text-slate-600 hover:bg-slate-100">+ {m.name}</button>
          ))}
        </div>
      )}

      <div className="mt-8 flex justify-end">
        <Totals rows={[
          [cfg.station?.pos_name || "Vivo", money(num(entry.attendant_expected?.[a.id]))],
          ...(line.nonfuel_collected ? [["Less lubes & LPG", money(line.nonfuel_collected)]] : []),
          ["Fuel to account for", money(line.expected_total)],
          ["Handed in", money(line.collected_total)],
          ["Over / short", signed(line.variance), tone(line.variance, cfg.station?.cash_tolerance || 100)],
        ]} />
      </div>
    </>
  );
}

function ExpenseLines({ doc, filter = () => true, newRow = {} }) {
  const { cfg, entry, update, readOnly } = doc;
  const cats = (cfg.expense_categories || []).filter((c) => c.active !== false);
  const rows = entry.expenses.map((e, i) => ({ ...e, i })).filter(filter);
  return (
    <>
      <Table minWidth={640} cols={[{ label: "Category" }, { label: "Details" }, { label: "Paid by" }, { label: "Amount", align: "right" }, { label: "" }]}>
        {rows.map((e) => (
          <Tr key={e._k}>
            <Td><select aria-label="Category" disabled={readOnly} value={e.category || ""} onChange={(ev) => { const v = ev.target.value; update((d) => { d.expenses[e.i].category = v; }); }} className="min-h-[34px] rounded-md border border-slate-300 bg-white px-1.5 text-sm"><option value="">—</option>{cats.map((c) => <option key={c.id} value={c.label}>{c.label}</option>)}</select></Td>
            <Td><TextIn label="Details" value={e.description} disabled={readOnly} onChange={(v) => update((d) => { d.expenses[e.i].description = v; })} /></Td>
            <Td><select aria-label="Paid by" disabled={readOnly} value={e.paid_by || ""} onChange={(ev) => { const v = ev.target.value; update((d) => { d.expenses[e.i].paid_by = v || null; }); }} className="min-h-[34px] rounded-md border border-slate-300 bg-white px-1.5 text-sm"><option value="">Shop</option>{cfg.attendants.filter((a) => a.active !== false).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Td>
            <Td right><NumIn label="Amount" value={e.amount} disabled={readOnly} onChange={(v) => update((d) => { d.expenses[e.i].amount = v; })} /></Td>
            <Td>{!readOnly && <button type="button" aria-label="Remove expense" className="min-h-[34px] px-2 text-slate-400 hover:text-rose-600" onClick={() => update((d) => { d.expenses.splice(e.i, 1); })}>×</button>}</Td>
          </Tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={5} className="px-2 py-4 text-slate-400">None</td></tr>}
      </Table>
      {!readOnly && <Btn kind="link" onClick={() => update((d) => { d.expenses.push({ _k: uid(), category: "", description: "", voucher_no: "", paid_by: null, amount: "", ...newRow }); })}>+ Add expense</Btn>}
    </>
  );
}

// ---------- lubes & LPG, tills, expenses ----------
function StockView({ doc }) {
  const { cfg, entry, computed, update, readOnly } = doc;
  const [cat, setCat] = useState("lube");
  const [all, setAll] = useState(false);
  const items = Object.fromEntries(cfg.nonfuel_items.map((i) => [i.id, i]));
  const att = Object.fromEntries(cfg.attendants.map((a) => [a.id, a]));
  const shop = !!cfg.station?.lube_shop_front && cat === "lube";
  const lines = Object.fromEntries(computed.nonfuel_lines.map((l) => [l.item_id, l]));
  const inCat = entry.nonfuel.map((r, i) => ({ ...r, i })).filter((r) => (cat === "lube" ? items[r.item_id]?.category === "lube" : items[r.item_id]?.category !== "lube"));
  const rows = all ? inCat : inCat.filter((r) => (lines[r.item_id]?.sold || 0) !== 0 || num(r.received) || num(r.returned));
  const group = computed.nonfuel_group_lines.find((g) => g.group === (cat === "lube" ? "lube" : "lpg_accessory")) || {};
  const collectors = computed.nonfuel_payment_lines.filter((l) => l.group === (cat === "lube" ? "lube" : "lpg_accessory") && l.amount);
  const tillLines = Object.fromEntries(computed.till_lines.map((l) => [l.till_id, l]));
  const tills = Object.fromEntries((cfg.tills || []).map((x) => [x.id, x]));
  const onShift = useLookups(doc).onShift;

  return (
    <>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" className="flex gap-1 rounded-md bg-slate-100 p-1">
          {[["lube", "Lubes"], ["lpg", "LPG"]].map(([v, l]) => (
            <button key={v} type="button" role="tab" aria-selected={cat === v} onClick={() => setCat(v)} className={`min-h-[34px] rounded px-3 text-[13px] ${cat === v ? "bg-white font-semibold text-slate-900" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
        <div role="tablist" className="flex gap-1 rounded-md bg-slate-100 p-1">
          <button type="button" role="tab" aria-selected={!all} onClick={() => setAll(false)} className={`min-h-[34px] rounded px-3 text-[13px] ${!all ? "bg-white font-semibold text-slate-900" : "text-slate-500"}`}>Sold</button>
          <button type="button" role="tab" aria-selected={all} onClick={() => setAll(true)} className={`min-h-[34px] rounded px-3 text-[13px] ${all ? "bg-white font-semibold text-slate-900" : "text-slate-500"}`}>All items ({inCat.length})</button>
        </div>
      </div>
      <Table minWidth={820} cols={[{ label: "Item" }, { label: shop ? "Opening forecourt" : "Opening", align: "right" }, ...(shop ? [{ label: "Opening shop", align: "right" }] : []),
        { label: cat === "lube" ? "From store" : "Delivered", align: "right" }, ...(cat !== "lube" ? [{ label: "Returned", align: "right" }] : []),
        { label: shop ? "Closing forecourt" : "Closing", align: "right" }, ...(shop ? [{ label: "Closing shop", align: "right" }] : []),
        { label: "Sold", align: "right" }, { label: "Price", align: "right" }, { label: "Amount", align: "right" }]}
        foot={<tr><td className="px-2 py-2.5" colSpan={shop ? 6 : cat !== "lube" ? 5 : 4}>Total</td><td className="px-2 py-2.5 text-right tabular-nums">{rows.reduce((a, r) => a + (lines[r.item_id]?.sold || 0), 0)}</td><td /><td className="px-2 py-2.5 text-right tabular-nums">{money(group.sold_revenue || 0)}</td></tr>}>
        {rows.map((r) => {
          const l = lines[r.item_id] || {};
          return (
            <Tr key={r.item_id}>
              <Td>{items[r.item_id]?.name}</Td>
              <Td right>{r._openingLocked || readOnly ? <span className="text-slate-500">{num(r.opening)}</span> : <NumIn label="Opening" value={r.opening} width="w-16" onChange={(v) => update((d) => { d.nonfuel[r.i].opening = v; })} />}</Td>
              {shop && <Td right>{r._shopOpeningLocked || readOnly ? <span className="text-slate-500">{num(r.shop_opening)}</span> : <NumIn label="Opening shop" value={r.shop_opening} width="w-16" onChange={(v) => update((d) => { d.nonfuel[r.i].shop_opening = v; })} />}</Td>}
              <Td right><NumIn label="From store" value={r.received} disabled={readOnly} width="w-16" onChange={(v) => update((d) => { d.nonfuel[r.i].received = v; })} /></Td>
              {cat !== "lube" && <Td right><NumIn label="Returned" value={r.returned} disabled={readOnly} width="w-16" onChange={(v) => update((d) => { d.nonfuel[r.i].returned = v; })} /></Td>}
              <Td right><NumIn label="Closing" value={r.closing} disabled={readOnly} width="w-16" onChange={(v) => update((d) => { d.nonfuel[r.i].closing = v; })} /></Td>
              {shop && <Td right><NumIn label="Closing shop" value={r.shop_closing} disabled={readOnly} width="w-16" onChange={(v) => update((d) => { d.nonfuel[r.i].shop_closing = v; })} /></Td>}
              <Td right className={(l.sold || 0) < 0 ? "font-semibold text-rose-700" : ""}>{l.sold || 0}</Td>
              <Td right>{money(l.unit_price || 0)}</Td>
              <Td right>{money(l.revenue || 0)}</Td>
            </Tr>
          );
        })}
        {rows.length === 0 && <tr><td colSpan={9} className="px-2 py-6 text-center text-slate-400">Nothing sold</td></tr>}
      </Table>
      <div className="mt-4 flex justify-end">
        <Totals rows={[
          ...collectors.map((c) => [att[c.attendant_id]?.name || "—", money(c.amount)]),
          ["Cash collected", money(collectors.reduce((a, c) => a + c.amount, 0))],
          ["Over / short", signed(group.variance || 0), tone(group.variance || 0)],
        ]} />
      </div>

      <h2 className="mt-10 text-xl font-semibold text-slate-900">M-Pesa tills</h2>
      <Table minWidth={980} cols={[{ label: "Till" }, { label: "Attendants" }, { label: "Opening", align: "right" }, { label: "Received", align: "right" }, { label: "Withdrawn", align: "right" },
        { label: "Should be", align: "right" }, { label: "Phone balance", align: "right" }, { label: "Difference", align: "right" }]}>
        {entry.till_ledger.map((r, i) => {
          const l = tillLines[r.till_id] || {};
          const on = entry.till_attendants?.[r.till_id] || [];
          return (
            <Tr key={r.till_id}>
              <Td>{tills[r.till_id]?.label}</Td>
              <Td>
                <div className="flex flex-wrap gap-1">
                  {onShift.map((a) => {
                    const sel = on.includes(a.id);
                    return <button key={a.id} type="button" disabled={readOnly} aria-pressed={sel}
                      onClick={() => update((d) => { const cur = d.till_attendants[r.till_id] || []; d.till_attendants[r.till_id] = sel ? cur.filter((x) => x !== a.id) : [...cur, a.id]; })}
                      className={`rounded-full px-2 py-0.5 text-xs ${sel ? "bg-brand/15 font-semibold text-slate-900" : "text-slate-400 hover:bg-slate-100"} ${readOnly && !sel ? "hidden" : ""}`}>{a.name}</button>;
                  })}
                </div>
              </Td>
              <Td right>{r._openingLocked || readOnly ? <span className="text-slate-500">{money(num(r.opening_balance))}</span> : <NumIn label="Opening" value={r.opening_balance} onChange={(v) => update((d) => { d.till_ledger[i].opening_balance = v; })} />}</Td>
              <Td right>{money(l.mpesa_received || 0)}</Td>
              <Td right><NumIn label="Withdrawn" value={r.withdrawal} disabled={readOnly} onChange={(v) => update((d) => { d.till_ledger[i].withdrawal = v; })} /></Td>
              <Td right>{money(l.closing_balance || 0)}</Td>
              <Td right><NumIn label="Phone balance" value={r.actual_balance} disabled={readOnly} onChange={(v) => update((d) => { d.till_ledger[i].actual_balance = v; })} /></Td>
              <Td right className={l.variance == null ? "text-slate-400" : tone(l.variance)}>{l.variance == null ? "—" : signed(l.variance)}</Td>
            </Tr>
          );
        })}
      </Table>
      {(computed.till_transfer_suggestions || []).filter((sg) => !entry.till_transfers.some((x) => x.landed_till_id === sg.landed_till_id && x.meant_till_id === sg.meant_till_id && Math.abs(num(x.amount) - sg.amount) < 0.5)).map((sg, k) => (
        <div key={k} className="mt-3 flex flex-wrap items-center gap-3 rounded-md bg-amber-50 px-3 py-2 text-sm">
          <span>{money(sg.amount)} paid into {tills[sg.landed_till_id]?.label} for {tills[sg.meant_till_id]?.label}</span>
          {!readOnly && <Btn kind="outline" onClick={() => update((d) => { d.till_transfers.push({ _k: uid(), landed_till_id: sg.landed_till_id, meant_till_id: sg.meant_till_id, amount: sg.amount, reference: sg.reference || "", note: "Confirmed" }); })}>Confirm wrong-till payment</Btn>}
        </div>
      ))}
      {entry.till_transfers.length > 0 && (
        <Table minWidth={600} cols={[{ label: "Wrong-till payment" }, { label: "Paid into" }, { label: "Meant for" }, { label: "Amount", align: "right" }, { label: "" }]}>
          {entry.till_transfers.map((x, i) => (
            <Tr key={x._k}>
              <Td>{x.reference || x.note || "—"}</Td><Td>{tills[x.landed_till_id]?.label}</Td><Td>{tills[x.meant_till_id]?.label}</Td>
              <Td right>{money(num(x.amount))}</Td>
              <Td>{!readOnly && <button type="button" aria-label="Remove" className="min-h-[34px] px-2 text-slate-400 hover:text-rose-600" onClick={() => update((d) => { d.till_transfers.splice(i, 1); })}>×</button>}</Td>
            </Tr>
          ))}
        </Table>
      )}

      <h2 className="mt-10 text-xl font-semibold text-slate-900">Expenses</h2>
      <ExpenseLines doc={doc} />

      <AccountPayments doc={doc} />
    </>
  );
}

function AccountPayments({ doc }) {
  const { cfg, entry, update, readOnly } = doc;
  const accounts = (cfg.receivable_accounts || []).filter((a) => a.active !== false);
  const methods = cfg.payment_methods.filter((m) => m.active !== false && ["cash", "mobile_money", "card"].includes(m.kind));
  if (readOnly && !entry.account_receipts.length) return null;
  return (
    <>
      <h2 className="mt-10 text-xl font-semibold text-slate-900">Customer payments received</h2>
      <Table minWidth={700} cols={[{ label: "Customer" }, { label: "Paid by" }, { label: "Reference" }, { label: "Amount", align: "right" }, { label: "" }]}>
        {entry.account_receipts.map((x, i) => (
          <Tr key={x._k}>
            <Td><select aria-label="Customer" disabled={readOnly} value={x.account_id || ""} onChange={(e) => { const v = e.target.value; update((d) => { d.account_receipts[i].account_id = v; }); }} className="min-h-[34px] rounded-md border border-slate-300 bg-white px-1.5 text-sm"><option value="">—</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Td>
            <Td><select aria-label="Paid by" disabled={readOnly} value={x.method_id || ""} onChange={(e) => { const v = e.target.value; update((d) => { d.account_receipts[i].method_id = v; }); }} className="min-h-[34px] rounded-md border border-slate-300 bg-white px-1.5 text-sm"><option value="">—</option>{methods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></Td>
            <Td><TextIn label="Reference" value={x.reference} disabled={readOnly} onChange={(v) => update((d) => { d.account_receipts[i].reference = v; })} /></Td>
            <Td right><NumIn label="Amount" value={x.amount} disabled={readOnly} onChange={(v) => update((d) => { d.account_receipts[i].amount = v; })} /></Td>
            <Td>{!readOnly && <button type="button" aria-label="Remove" className="min-h-[34px] px-2 text-slate-400 hover:text-rose-600" onClick={() => update((d) => { d.account_receipts.splice(i, 1); })}>×</button>}</Td>
          </Tr>
        ))}
        {entry.account_receipts.length === 0 && <tr><td colSpan={5} className="px-2 py-4 text-slate-400">None</td></tr>}
      </Table>
      {!readOnly && <Btn kind="link" onClick={() => update((d) => { d.account_receipts.push({ _k: uid(), account_id: "", method_id: "", till_id: "", amount: "", reference: "", note: "" }); })}>+ Add payment</Btn>}
      <div className="h-10" />
    </>
  );
}
